import * as React from "react";
import { cn } from "../../lib/utils";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "./table";
import { Badge } from "./badge";
import { StatusBadge } from "./status-badge";
import { Button } from "./button";
import { Switch } from "./switch";
import { Skeleton } from "./skeleton";
import { MobileTransporterCard } from "./mobile-transporter-card";
import { ResponsiveTableContainer } from "./responsive-table-container";
import { 
  MapPin, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Loader2,
  Users,
  Truck,
  AlertTriangle
} from "lucide-react";
import { EnhancedTransporter } from "../../lib/api";

interface ResponsiveTransporterListProps {
  transporters: EnhancedTransporter[];
  isLoading?: boolean;
  onView: (id: string) => void;
  onEdit: (transporter: EnhancedTransporter) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
  onVerify: (id: string) => void;
  isDeleting?: string | null;
  isToggling?: string | null;
  className?: string;
}

const ResponsiveTransporterList = React.forwardRef<
  HTMLDivElement,
  ResponsiveTransporterListProps
>(({ 
  transporters, 
  isLoading = false,
  onView, 
  onEdit, 
  onDelete, 
  onToggleActive, 
  onVerify,
  isDeleting = null,
  isToggling = null,
  className,
  ...props 
}, ref) => {
  
  // Helper function to check if transporter is verified
  const isVerified = (transporter: EnhancedTransporter) => {
    return transporter.verificationStatus === 'verified';
  };

  return (
    <div ref={ref} className={cn("w-full", className)} {...props}>
      {/* Desktop/Tablet Table View - hidden on mobile */}
      <div className="hidden md:block">
        <ResponsiveTableContainer maxHeight="none">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Performance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell>
                      <div className="flex space-x-2 justify-end">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : transporters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center space-y-2">
                      <Truck className="h-12 w-12 text-muted-foreground" />
                      <p className="text-lg font-medium">No transporters found</p>
                      <p className="text-sm text-muted-foreground">Add your first transporter to get started</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                transporters.map((transporter) => (
                  <TableRow 
                    key={transporter._id} 
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => onView(transporter._id)}
                  >
                    <TableCell className="font-medium">{transporter.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{transporter.email}</span>
                        <span className="text-sm text-muted-foreground">{transporter.mobile}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-muted-foreground" />
                        {transporter.address?.city}, {transporter.address?.state}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">Rating: {transporter.rating || 'N/A'}</span>
                        <span className="text-sm text-muted-foreground">
                          Trips: {transporter.completedTrips || 0}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={transporter.isActive ? 'active' : 'inactive'} />
                        <Switch
                          checked={transporter.isActive}
                          onCheckedChange={() => onToggleActive(transporter._id)}
                          disabled={isToggling === transporter._id}
                          aria-label={`Toggle ${transporter.name} active status`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={transporter.verificationStatus} />
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1 justify-end">
                        {transporter.verificationStatus === 'pending' && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              onVerify(transporter._id);
                            }}
                            aria-label={`Verify ${transporter.name}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(transporter);
                          }}
                          aria-label={`Edit ${transporter.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(transporter._id);
                          }}
                          disabled={isDeleting === transporter._id}
                          aria-label={`Delete ${transporter.name}`}
                        >
                          {isDeleting === transporter._id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTableContainer>
      </div>

      {/* Mobile Card View - visible only on mobile */}
      <div className="block md:hidden h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-1">
            {isLoading ? (
              // Loading skeleton cards
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                    <div className="flex space-x-1">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </div>
                </div>
              ))
            ) : transporters.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <Truck className="h-16 w-16 text-muted-foreground" />
                  <div className="space-y-2">
                    <p className="text-xl font-medium">No transporters found</p>
                    <p className="text-sm text-muted-foreground">Add your first transporter to get started</p>
                  </div>
                </div>
              </div>
            ) : (
              transporters.map((transporter) => (
                <MobileTransporterCard
                  key={transporter._id}
                  transporter={transporter}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleActive={onToggleActive}
                  onVerify={onVerify}
                  isDeleting={isDeleting === transporter._id}
                  isToggling={isToggling === transporter._id}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ResponsiveTransporterList.displayName = "ResponsiveTransporterList";

export { ResponsiveTransporterList };